import queryString from 'query-string'
import React, { useEffect, useState } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Field, Form, getFormValues, reduxForm } from 'redux-form'

import { Button, FormHelperText, Grid, Link, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import ErrorMessage from '../../components/ErrorMessage'
import CountdownConditional from '../../components/Conditional/CountdownConditional'
import withInit from '../../components/containers/withInit'
import twoFactorFields from '../../config/two-factor-fields'
import validationService from '../../services/validation-service'
import { verifyTwoFactor, requestNewTwoFactor } from '../../utils/helpers'
import { twoFactorValidationSchema } from '../../utils/validationSchema'
import styles from './TwoFactorFormStyles'

const useStyles = makeStyles(styles)

const TwoFactorForm = props => {
  const classes = useStyles()
  const { t } = useTranslation('pageText')
  const [showTwoFactor, setShowTwoFactor] = useState(false)
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorStatus, setTwoFactorStatus] = useState("idle");
  const [isTwoFactorComplete, setIsTwoFactorComplete] = useState(false)

  const location = useLocation()
  const { partnerId = '' } = useParams()

  const {
    formValues,
    error,
    handleSubmit,
    asyncValidating,
    pristine,
    valid,
    localisation,
    submitting,
    setIsSubmitting,
    isSubmitting,
    change,
    submit,
    submitSucceeded
  } = props

  const submitDisabled = !!(pristine || !valid || asyncValidating)
  const qs = queryString.parse(location.search)
  const { Email, ReturnUrl } = location.state || {};

  const rtMapper = {
    '2': {
      message: 'Invalid or expired two-factor authentication code.',
      paperClassName: 'errorMessage',
      textClassName: 'errorHelper',
    },
  }

  const hasQueryRT = qs.rt || undefined

  const RTMessage = ({ message, paperClassName, textClassName }) => (
    <Grid container>
      <Grid item container md={12} sm={12} xs={12}>
        <Paper elevation={0} className={classes[paperClassName]}>
          <FormHelperText className={classes[textClassName]}>{message}</FormHelperText>
        </Paper>
      </Grid>
    </Grid>
  )

  useEffect(() => {
    document.title = t('pageTitle')
  }, [t])

  useEffect(() => {
    if (submitting) {
      setIsSubmitting(true)
    }
  }, [submitting, setIsSubmitting])

  useEffect(() => {
    if (submitSucceeded) {
      setTwoFactorStatus('success');
    }
  }, [submitSucceeded]);

  useEffect(() => {
    if (error) {
      setIsSubmitting(false);
      setTwoFactorStatus('error');
    }
  }, [error, setIsSubmitting])

  const fields = twoFactorFields(localisation.pageText)
  return (
    <>
      <Typography component="h3" variant="h5" color="primary" className={classes.title}>
        Two-factor authentication code
      </Typography>

      <p>Enter the two-factor authentication code sent to your email.</p>

      <Form className={classes.form} onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item md={12} sm={12} xs={12}>
            <Field
              {...fields.twoFactorCode}
              status={twoFactorStatus}
              onIsCompleteChange={setIsTwoFactorComplete}
              onChange={(code) => { 
                change('twoFactorCode', '');
                setTwoFactorStatus('idle'); 
              }}
              onComplete={async (code) => {
                change('twoFactorCode', code)
                setTwoFactorStatus('loading'); 
                await handleSubmit((values, dispatch, opts) => verifyTwoFactor({ ...values, twoFactorCode: code }, dispatch, opts))()
              }}
            />
          </Grid>
          {hasQueryRT && <RTMessage {...rtMapper[qs.rt]} />}
          <ErrorMessage error={error} />

          <Grid item container md={12} sm={12} xs={12}>
            <Button
              id="verifyButton"
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              color="primary"
              className={classes.submit}
              disabled={!isTwoFactorComplete || isSubmitting || twoFactorStatus === 'success'}
            >
              {t('verify')}
            </Button>
          </Grid>

          <Grid container item md={12} xs={12} direction="row" justifyContent="space-between" alignItems="center">
            <Field {...fields.trustDevice}>{fields.trustDevice.label}</Field>
          </Grid>
        </Grid>

        <div>
          <p class={styles.noCodeMessage}>
            Didn't receive a code?&nbsp;
            <CountdownConditional
              seconds={120}
              pendingText="Resend in {countdown}"
            >
              {({ resetCountdown }) => (
                <Link href="#" className={classes.link} onClick={() => { 
                  requestNewTwoFactor(); 
                  setTwoFactorCode(''); 
                  resetCountdown(); 
                  }}>
                  Resend
                </Link>
              )}
            </CountdownConditional>.
          </p>
        </div>
      </Form>
    </>
  )
}

const ReduxTwoFactorForm = reduxForm({
  form: 'twoFactorForm',
  validate: validationService.syncValidation(twoFactorValidationSchema),
  onSubmit: verifyTwoFactor,
  enableReinitialize: true,
})(TwoFactorForm)

const mapStateToProps = state => {
  const {
    form: { twoFactorForm },
  } = state

  const initialValues = {
  }

  return {
    formValues: getFormValues('twoFactorForm')(state),
    twoFactorForm,
    initialValues,
  }
}

const connectedTwoFactorForm = connect(mapStateToProps)(ReduxTwoFactorForm)

export default withInit(connectedTwoFactorForm)
